'use client'

import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, CreditCard, Loader2, ShieldCheck } from 'lucide-react'
import { useRouter } from 'next/navigation'

import { CheckoutFormBlock } from '@/components/funnel/blocks/CheckoutFormBlock'
import { buildPurchaseContext } from '@/lib/checkout/buildPurchaseContext'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { PlanCard } from '@/components/platform/setup/PlanCard'

type MembershipRow = {
  groupid: number
  product_id: number | null
  gprice: string | null
  price: string | null
  expiry: number | null
  recurring_id: number | null
  trial_status: number | null
  group_image: string | null

  defaultCategoryProduct: number | null
  defaultCategoryQuantity: number | null
  defaultCategoryPrice: number | null
  defaultNoOfListing: number | null
  defaultListingDuration: number | null
  defaultCourse: number | null
  defaultLesson: number | null
  defaultChannel: number | null
  defaultMember: number | null
  defaultFunnel: number | null
  defaultEmails: number | null
  defaultAICredits: number | null
  defaultBrainTasks: number | null
  defaultStorage: number | null
  defaultHower: number | null
  defaultGroupCommissionPercentage: number | null
  defaultGroupCommissionFixed: number | null
  defaultListingFee: number | null

  autoApprove?: number | null
  group_tag_id?: number | null
  sort: number
  status: number
  name: string
}

type Step = 'plans' | 'checkout'

function money(n: number, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(n)
  } catch {
    return `$${Math.round(n)}`
  }
}

function parsePrice(row: MembershipRow): number {
  const raw = (row.gprice ?? row.price ?? '').toString().trim()
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

function intervalFromExpiry(expiry?: number | null): 'month' | 'year' {
  const d = Number(expiry || 0)
  if (d === 365) return 'year'
  return 'month'
}

function daysToLabel(days?: number | null) {
  const d = Number(days || 0)

  if (!d) return 'Sin expiración'
  if (d === 30) return 'Mensual'
  if (d === 365) return 'Anual'

  return `${d} días`
}

function findMonthlyMembership(memberships: MembershipRow[]) {
  return (
    memberships.find((m) => Number(m.status) === 1 && Number(m.expiry) === 30) ||
    memberships.find(
      (m) =>
        Number(m.status) === 1 &&
        intervalFromExpiry(m.expiry) === 'month'
    ) ||
    null
  )
}

function findYearlyMembership(memberships: MembershipRow[]) {
  return (
    memberships.find((m) => Number(m.status) === 1 && Number(m.expiry) === 365) ||
    memberships.find(
      (m) =>
        Number(m.status) === 1 &&
        intervalFromExpiry(m.expiry) === 'year'
    ) ||
    null
  )
}

function getFeatures(m: MembershipRow) {
  const list: string[] = []

  list.push(
    Number(m.defaultMember || 0) > 0
      ? `${m.defaultMember} miembros incluidos`
      : 'Miembros ilimitados'
  )

  list.push(
    Number(m.defaultFunnel || 0) > 0
      ? `${m.defaultFunnel} embudos incluidos`
      : 'Embudos ilimitados'
  )

  list.push(
    Number(m.defaultCourse || 0) > 0
      ? `${m.defaultCourse} cursos incluidos`
      : 'Cursos ilimitados'
  )

  if (Number(m.defaultEmails || 0) > 0) {
    list.push(`${m.defaultEmails} correos a miembros`)
  }

  if (Number(m.defaultAICredits || 0) > 0) {
    list.push(`${m.defaultAICredits} créditos de IA`)
  }

  if (Number(m.defaultBrainTasks || 0) > 0) {
    list.push(`${m.defaultBrainTasks} tareas programadas`)
  }

  if (Number(m.defaultStorage || 0) > 0) {
    list.push(`${m.defaultStorage} GB de almacenamiento`)
  }

  if (!list.length) {
    list.push('Acceso Business')
  }

  return list.slice(0, 5)
}

function buildMembershipProduct(selected: MembershipRow | null) {
  if (!selected) return null

  const price = parsePrice(selected)
  const recurringId = Number(selected.recurring_id || 0) || 1
  const interval = intervalFromExpiry(selected.expiry)

  return {
    product_id: Number(selected.product_id || selected.groupid),
    name: selected.name,
    seller_id: 0,
    currency: 'USD',
    whitelabel: true,

    paymentMethods: {
      hasStripe: true,
      hasCrypto: false,
      cryptoWallet: null,
      cryptoChain: null,
      cryptoToken: null,
    },

    recurrings: [
      {
        recurring_id: recurringId,
        price,
        frequency: interval,
        cycle: 1,
        trial: {
          enabled: Boolean(Number(selected.trial_status || 0)),
          price: 0,
          frequency: 'day',
          cycle: 0,
        },
      },
    ],
  }
}

export function BusinessMembershipPaymentModal() {
  const router = useRouter()
  const { user, isLoading: userLoading } = useCurrentUser()

  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<Step>('plans')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [platformName, setPlatformName] = useState('')
  const [memberships, setMemberships] = useState<MembershipRow[]>([])
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly' | null>(null)
  const [selectedMembership, setSelectedMembership] = useState<MembershipRow | null>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    function handleOpen() {
      setOpen(true)
      setStep('plans')
      setError(null)
    }

    window.addEventListener('hubee:business_membership_payment_open', handleOpen)

    return () => {
      window.removeEventListener('hubee:business_membership_payment_open', handleOpen)
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    let cancelled = false

    async function loadMemberships() {
      setLoading(true)
      setError(null)

      try {
        const res = await fetch('/api/platform/memberships', {
          cache: 'no-store',
          credentials: 'include',
        })

        const data = await res.json().catch(() => null)

        if (!res.ok || !data?.success) {
          throw new Error(data?.error || 'MEMBERSHIPS_LOAD_FAILED')
        }

        if (cancelled) return

        setPlatformName(String(data.platformName || 'Tu plataforma'))
        setMemberships(Array.isArray(data.memberships) ? data.memberships : [])
      } catch (error: any) {
        console.error('[BusinessMembershipPaymentModal]', error)

        if (!cancelled) {
          setError('No pudimos cargar las membresías disponibles.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadMemberships()

    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const monthlyMembership = useMemo(
    () => findMonthlyMembership(memberships),
    [memberships]
  )

  const yearlyMembership = useMemo(
    () => findYearlyMembership(memberships),
    [memberships]
  )

  const membershipProduct = useMemo(() => {
    return buildMembershipProduct(selectedMembership)
  }, [selectedMembership])

  const activeRecurring = useMemo(() => {
    if (!membershipProduct || !selectedMembership) return null

    const rid = Number(selectedMembership.recurring_id || 0) || 1

    return (
      membershipProduct.recurrings?.find(
        (r: any) => Number(r.recurring_id) === rid
      ) || null
    )
  }, [membershipProduct, selectedMembership])

  const checkoutData = useMemo(() => {
    if (!membershipProduct || !activeRecurring || !selectedMembership) return null

    const price =
      Number(activeRecurring.price || 0) || parsePrice(selectedMembership)

    return buildPurchaseContext({
      product: membershipProduct,
      activeRecurring,
      selectedRecurringId:
        Number(activeRecurring.recurring_id || 0) || 1,
      price,
      priceUSD: price,
      paymentMethods: membershipProduct.paymentMethods,

      platform: {
        enabled: true,
        kind: 'membership',
        group_id: Number(selectedMembership.groupid),
        platform_name: platformName,
      } as any,
    })
  }, [membershipProduct, activeRecurring, selectedMembership, platformName])

  function close() {
    setOpen(false)
    setStep('plans')
    setSelectedPlan(null)
    setSelectedMembership(null)
    setError(null)
  }

  function handleSelectMembership(plan: 'monthly' | 'yearly') {
    const membership =
      plan === 'monthly' ? monthlyMembership : yearlyMembership

    if (!membership) return

    setSelectedPlan(plan)
    setSelectedMembership(membership)
    setStep('checkout')
  }

  function selectedPriceLabel() {
    if (!selectedMembership) return '$0'
    return money(parsePrice(selectedMembership), 'USD')
  }

  function selectedPeriodLabel() {
    if (!selectedMembership) return ''

    return intervalFromExpiry(selectedMembership.expiry) === 'year'
      ? '/ año'
      : '/ mes'
  }

  if (!mounted || !open) return null

  return createPortal(
    <div className="fixed inset-0 z-[10000]">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={close}
      />

      <div className="relative z-10 flex min-h-dvh w-full items-center justify-center overflow-y-auto p-4 md:p-6">
        <div className="relative w-full max-w-3xl rounded-[32px] border border-black/5 bg-white text-zinc-950 shadow-2xl">
          <button
            type="button"
            onClick={close}
            className="absolute right-5 top-5 z-20 rounded-full bg-zinc-100 p-2 text-zinc-500 transition hover:bg-zinc-200 hover:text-black"
          >
            <X className="h-5 w-5" />
          </button>

          {step === 'plans' && (
            <div className="p-6 md:p-10">
              <div className="mb-8 pr-12">
                <div className="mb-3 inline-flex rounded-full bg-[#FACC15]/10 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-zinc-900">
                  Renovar membresía
                </div>

                <h2 className="text-3xl font-black tracking-tighter md:text-5xl">
                  Mantén activa tu comunidad
                </h2>

                <p className="mt-3 max-w-2xl text-sm font-medium leading-relaxed text-zinc-500 md:text-base">
                  Renueva tu acceso antes de la fecha de vencimiento para evitar que tu comunidad se pause temporalmente.
                </p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center rounded-3xl bg-zinc-50 px-4 py-12 text-sm font-bold text-zinc-400">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cargando membresías…
                </div>
              ) : error ? (
                <div className="rounded-3xl bg-red-50 px-6 py-8 text-center text-sm font-bold text-red-500">
                  {error}
                </div>
              ) : !memberships.length ? (
                <div className="rounded-3xl bg-zinc-50 px-6 py-8 text-center text-sm font-bold text-zinc-400">
                  No hay membresías activas disponibles.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {monthlyMembership && (
                    <PlanCard
                      title={monthlyMembership.name || 'Mensual'}
                      price={money(parsePrice(monthlyMembership), 'USD')}
                      period="/ mes"
                      desc="Ideal para mantener activa tu comunidad mes a mes."
                      features={getFeatures(monthlyMembership)}
                      meta={daysToLabel(monthlyMembership.expiry)}
                      onClick={() => handleSelectMembership('monthly')}
                    />
                  )}

                  {yearlyMembership && (
                    <PlanCard
                      title={yearlyMembership.name || 'Anual'}
                      price={money(parsePrice(yearlyMembership), 'USD')}
                      period="/ año"
                      desc="Asegura tu plataforma con visión de largo plazo."
                      highlight
                      badge="Mejor valor"
                      features={getFeatures(yearlyMembership)}
                      meta={daysToLabel(yearlyMembership.expiry)}
                      onClick={() => handleSelectMembership('yearly')}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'checkout' && (
            <div className="p-6 md:p-10">
              <div className="mb-8 flex items-start justify-between gap-6 pr-12">
                <div className="hidden">
                  <div className="mb-3 inline-flex rounded-full bg-[#FACC15]/10 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-zinc-900">
                    Pago seguro
                  </div>

                  <h2 className="text-3xl font-black tracking-tighter md:text-5xl">
                    Finaliza tu renovación
                  </h2>

                  <p className="mt-3 text-sm font-medium leading-relaxed text-zinc-500 md:text-base">
                    Al completar el pago, tu membresía quedará actualizada.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setStep('plans')}
                  className="hidden shrink-0 rounded-2xl bg-zinc-100 px-5 py-3 text-xs font-black uppercase tracking-widest text-zinc-500 transition-all hover:bg-zinc-200 hover:text-black md:block"
                >
                  Cambiar
                </button>
              </div>

              <div className="hidden mb-6 rounded-[28px] border border-zinc-100 bg-zinc-50 p-6">
                <div className="mb-5 flex items-center justify-between border-b border-zinc-200 pb-5">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                      Plan elegido
                    </span>

                    <span className="text-lg font-black uppercase italic tracking-tighter">
                      {selectedMembership?.name ||
                        `Hubee ${selectedPlan === 'monthly' ? 'Mensual' : 'Anual'}`}
                    </span>
                  </div>

                  <span className="text-2xl font-black">
                    {selectedPriceLabel()}
                    <span className="ml-1 text-sm font-medium text-zinc-400">
                      {selectedPeriodLabel()}
                    </span>
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-zinc-500">
                    Plataforma
                  </span>
                  <span className="text-sm font-black">
                    {platformName || 'Tu plataforma'}
                  </span>
                </div>
              </div>

              <div className="mb-6 flex flex-col gap-6 rounded-3xl border-2 border-zinc-100 bg-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-zinc-400" />
                    <span className="text-sm font-bold text-zinc-400">
                      Pagar con tarjeta
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <div className="h-5 w-8 rounded-sm bg-zinc-100" />
                    <div className="h-5 w-8 rounded-sm bg-zinc-100" />
                  </div>
                </div>

                {userLoading ? (
                  <div className="flex items-center justify-center rounded-2xl bg-zinc-50 px-4 py-8 text-sm font-bold text-zinc-400">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cargando usuario…
                  </div>
                ) : !user ? (
                  <div className="rounded-2xl bg-zinc-50 px-4 py-8 text-center text-sm font-bold text-zinc-400">
                    Inicia sesión para continuar con el pago.
                  </div>
                ) : !selectedMembership ? (
                  <div className="rounded-2xl bg-zinc-50 px-4 py-8 text-center text-sm font-bold text-zinc-400">
                    Selecciona una membresía.
                  </div>
                ) : !checkoutData ? (
                  <div className="rounded-2xl bg-zinc-50 px-4 py-8 text-center text-sm font-bold text-zinc-400">
                    Preparando checkout…
                  </div>
                ) : (
                  <CheckoutFormBlock
                    data={checkoutData}
                    isEditing={false}
                    onSuccess={() => {
                      close()
                      router.refresh()
                    }}
                  />
                )}
              </div>

              <p className="flex items-center justify-center gap-2 text-center text-[10px] font-black uppercase tracking-widest text-zinc-400">
                <ShieldCheck className="h-4 w-4" />
                Pago seguro procesado por Stripe
              </p>

              <button
                type="button"
                onClick={() => setStep('plans')}
                className="mt-5 w-full rounded-2xl bg-zinc-100 px-5 py-4 text-xs font-black uppercase tracking-widest text-zinc-500 transition-all hover:bg-zinc-200 hover:text-black md:hidden"
              >
                Cambiar membresía
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}